import { Link } from "react-router-dom";
import { useRef, useCallback } from "react";

// Lazy-init shared AudioContext (one per tab)
let audioCtx = null;
const getAudioCtx = () => {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtx = new Ctx();
    } catch {
      return null;
    }
  }
  return audioCtx;
};

// Synthesised lock-click — two short bursts to simulate the "tumbler" sound of a key turning.
const playKeyTurn = () => {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    const make = (startOffset, peakFreq, endFreq, peak = 0.09, dur = 0.09) => {
      const t = ctx.currentTime + startOffset;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(peakFreq, t);
      osc.frequency.exponentialRampToValueAtTime(endFreq, t + dur);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(peak, t + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + dur + 0.02);
    };
    // Two staggered clicks ≈ ignition turn → engagement
    make(0, 2600, 900, 0.1, 0.07);
    make(0.13, 1800, 600, 0.08, 0.09);
  } catch {
    /* silently ignore audio failures */
  }
};

export default function Logo({ size = "md" }) {
  const lastPlay = useRef(0);

  const onMouseEnter = useCallback(() => {
    const now = Date.now();
    if (now - lastPlay.current < 500) return; // debounce: avoid retrigger spam
    lastPlay.current = now;
    const ctx = getAudioCtx();
    if (ctx && ctx.state === "suspended") {
      ctx.resume().then(playKeyTurn).catch(() => {});
    } else {
      playKeyTurn();
    }
  }, []);

  const px = size === "lg" ? "text-3xl" : "text-xl";

  return (
    <Link
      to="/"
      data-testid="logo-link"
      onMouseEnter={onMouseEnter}
      className="flex items-center gap-3 group relative"
    >
      {/* Mark container — holds glow + SVG */}
      <div className="relative w-9 h-9 flex items-center justify-center">
        {/* Glow pulse — fires in sync with the 600ms key turn */}
        <span
          aria-hidden="true"
          className="absolute inset-[-6px] rounded-full bg-[#D4AF37] opacity-0 blur-xl scale-50 transition-all duration-[700ms] ease-out group-hover:opacity-40 group-hover:scale-150 pointer-events-none"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full ring-1 ring-[#D4AF37]/0 transition-all duration-[600ms] ease-out group-hover:ring-[#D4AF37]/60 group-hover:inset-[-4px] pointer-events-none"
        />

        {/* Key brand mark — pivots 90° around its bow on hover, like a key in a lock */}
        <svg
          width="36"
          height="36"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ transformOrigin: "32.5% 32.5%" }}
          className="relative transition-transform duration-[600ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:rotate-90"
        >
          {/* Bow (key head) — outer ring */}
          <circle cx="13" cy="13" r="7.5" stroke="#D4AF37" strokeWidth="2.2" />
          {/* Inner dot */}
          <circle cx="13" cy="13" r="2.3" fill="#D4AF37" />
          {/* Shaft (diagonal) */}
          <line
            x1="18.5"
            y1="18.5"
            x2="33"
            y2="33"
            stroke="#D4AF37"
            strokeWidth="2.6"
            strokeLinecap="round"
          />
          {/* Tooth notch */}
          <line
            x1="26"
            y1="28"
            x2="30"
            y2="24"
            stroke="#D4AF37"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
          {/* Highlight glint */}
          <circle cx="10.5" cy="10.5" r="1" fill="#F6ECCC" opacity="0.7" />
        </svg>
      </div>

      <div className="flex flex-col leading-none">
        <span
          className={`font-display ${px} text-white tracking-wide`}
          style={{ fontWeight: 400 }}
        >
          Keys to <span className="italic text-[#D4AF37]">Own</span>
        </span>
        <span className="text-[10px] tracking-[0.32em] text-[#D4AF37]/80 uppercase mt-1">
          Australia
        </span>
      </div>
    </Link>
  );
}
